import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_PROMPTED_KEY = "bizpa_push_prompted";
const LS_SUBSCRIBED_KEY = "bizpa_push_subscribed";

/* Convert VAPID base64url → Uint8Array */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export default function usePushNotifications() {
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // On mount: check if already subscribed
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (e) {
        // swallow — SW may not be ready on first load
      }
    })();
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) {
      setError("Push not supported on this device/browser");
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      localStorage.setItem(LS_PROMPTED_KEY, "1");
      if (perm !== "granted") {
        setError("Permission denied");
        setBusy(false);
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await axios.get(`${API}/push/vapid-public-key`);
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
      localStorage.setItem(LS_SUBSCRIBED_KEY, "1");
      setSubscribed(true);
      return true;
    } catch (e) {
      console.error("Push subscribe failed", e);
      setError(e.message || "Subscribe failed");
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios
          .post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint })
          .catch(() => {});
        await sub.unsubscribe();
      }
      localStorage.removeItem(LS_SUBSCRIBED_KEY);
      setSubscribed(false);
      return true;
    } catch (e) {
      setError(e.message || "Unsubscribe failed");
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/push/test`, {
        title: "KipaBiz",
        body: "Push test — if you see this, you're all set! 🎉",
      });
      return data;
    } catch (e) {
      setError(e.message || "Test failed");
      return null;
    }
  }, []);

  const shouldAutoPrompt = useCallback(() => {
    return (
      supported &&
      permission === "default" &&
      localStorage.getItem(LS_PROMPTED_KEY) !== "1"
    );
  }, [supported, permission]);

  return {
    supported,
    permission,
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
    sendTest,
    shouldAutoPrompt,
  };
}
